/**
 * PlanSelectionModal — full-screen, non-dismissible plan picker (Subscription V3).
 *
 * Clinic / Chain (self-serve) + Enterprise ("Talk to us"). Monthly /
 * Half-Yearly / Annual cycles from the DB pricing config, extra-location
 * and extra-user steppers, Founding Clinic badge with live spots-left, and
 * a server-side quote per plan (discount, one-time onboarding fee, GST).
 *
 * Non-dismissible for `pending_selection`; also opened on demand via
 * `window.dispatchEvent(new CustomEvent('audinexa:open-plan-modal'))`.
 * On successful Razorpay payment the backend auto-provisions the
 * subscription and this modal closes as soon as /my reflects it.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Sparkles, ShieldCheck, Building, Mail, MapPin, Users, X } from 'lucide-react';
import { EnterpriseEnquiryModal } from './EnterpriseEnquiryModal';
import { useSubscription } from '../SubscriptionContext';
import { useAuth } from '../AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const loadRazorpayScript = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
  if (existing) {
    existing.addEventListener('load', () => resolve());
    existing.addEventListener('error', () => reject(new Error('Razorpay SDK failed to load')));
    return;
  }
  const s = document.createElement('script');
  s.src = RAZORPAY_SCRIPT; s.async = true;
  s.onload = () => resolve();
  s.onerror = () => reject(new Error('Razorpay SDK failed to load'));
  document.body.appendChild(s);
});

export default function PlanSelectionModal() {
  const { user } = useAuth();
  const sub = useSubscription();
  const [intended] = useState(() => {
    try { return JSON.parse(localStorage.getItem('audinexa.intended_plan') || 'null'); } catch { return null; }
  });
  const [cycle, setCycle] = useState(intended?.cycle || 'monthly');
  const [extraLocs, setExtraLocs] = useState(0);
  const [extraUsers, setExtraUsers] = useState(0);
  const [smartMkt, setSmartMkt] = useState(false);
  const [matrix, setMatrix] = useState(null);
  const [quotes, setQuotes] = useState({});
  const [busyTier, setBusyTier] = useState(null);
  const [error, setError] = useState('');

  const status = sub?.v2?.status;
  const isOwner = user && ['clinic_owner', 'super_admin', 'founder'].includes(user.role);
  const isAdminSkip = user && ['super_admin', 'founder'].includes(user.role);
  const [forceOpen, setForceOpen] = useState(false);
  const [openedSnapshot, setOpenedSnapshot] = useState(null);
  const shouldShow = (status === 'pending_selection' || forceOpen) && !sub.loading;
  const snapshot = `${status}|${sub?.v2?.current_period_end || ''}|${sub?.v2?.tier || ''}`;
  const dismissible = forceOpen && !['pending_selection', 'expired'].includes(status);

  useEffect(() => {
    const onOpen = () => setForceOpen(true);
    window.addEventListener('audinexa:open-plan-modal', onOpen);
    return () => window.removeEventListener('audinexa:open-plan-modal', onOpen);
  }, []);
  // Close only once payment actually changed the subscription (status /
  // period end / tier), not merely because the tenant isn't pending.
  useEffect(() => {
    if (!forceOpen) { setOpenedSnapshot(null); return undefined; }
    if (openedSnapshot === null) { setOpenedSnapshot(snapshot); return undefined; }
    if (snapshot !== openedSnapshot && status !== 'pending_selection' && sub?.v2?.write_allowed) {
      const t = setTimeout(() => setForceOpen(false), 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [forceOpen, snapshot, openedSnapshot, status, sub?.v2?.write_allowed]);

  useEffect(() => {
    if (!shouldShow) return;
    setExtraLocs(sub?.v2?.extra_clinics_count || 0);
    setExtraUsers(sub?.v2?.extra_users_count || 0);
    axios.get(`${API}/subscriptions/pricing`).then((r) => setMatrix(r.data)).catch(() => setMatrix(null));
  }, [shouldShow]);

  // Server-side quotes (founding / locked price / onboarding fee / GST).
  useEffect(() => {
    if (!shouldShow) return undefined;
    const body = { billing_cycle: cycle, extra_clinics_count: extraLocs, extra_users_count: extraUsers, smart_marketing: smartMkt };
    const t = setTimeout(async () => {
      const out = {};
      await Promise.all(['clinic', 'chain'].map(async (tier) => {
        try {
          out[tier] = (await axios.post(`${API}/subscriptions/quote`, { ...body, tier })).data;
        } catch (e) {
          out[tier] = { error: e?.response?.data?.detail || 'Price unavailable' };
        }
      }));
      setQuotes(out);
    }, 250);
    return () => clearTimeout(t);
  }, [shouldShow, cycle, extraLocs, extraUsers, smartMkt]);

  useEffect(() => {
    if (!shouldShow) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); } };
    document.addEventListener('keydown', onKey, true);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = '';
    };
  }, [shouldShow]);

  const checkout = async (tier) => {
    if (busyTier) return;
    setBusyTier(tier); setError('');
    try {
      await loadRazorpayScript();
      const r = await axios.post(`${API}/subscriptions/checkout`, {
        tier, billing_cycle: cycle, extra_clinics_count: extraLocs,
        extra_users_count: extraUsers, smart_marketing: smartMkt,
      });
      const { razorpay, invoice_id, razorpay_configured } = r.data;
      if (!razorpay_configured || !razorpay) {
        setError('Payment gateway is not configured on this environment. Ask support to enable Razorpay.');
        setBusyTier(null);
        return;
      }
      const rzp = new window.Razorpay({
        key: razorpay.key_id,
        amount: razorpay.amount,
        currency: razorpay.currency,
        order_id: razorpay.order_id,
        name: 'AUDINEXA — Clinic OS',
        description: `${matrix?.tiers?.[tier]?.name || tier} plan · ${cycle.replace('_', ' ')}`,
        prefill: { name: user?.name || '', email: user?.email || '' },
        notes: { tenant_invoice_id: invoice_id, plan: tier, cycle },
        theme: { color: '#0F52BA' },
        modal: { ondismiss: () => setBusyTier(null), confirm_close: true },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/billing/tenant-invoices/${invoice_id}/razorpay/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setTimeout(() => sub.refresh && sub.refresh(), 800);
          } catch (e) {
            setError('Verification failed: ' + (e?.response?.data?.detail || e.message));
          } finally {
            setBusyTier(null);
          }
        },
      });
      rzp.on('payment.failed', (r2) => {
        const e = r2?.error || {};
        setError(`Payment failed: ${e.code || ''} ${e.description || ''}`);
        setBusyTier(null);
      });
      rzp.open();
    } catch (e) {
      setError('Could not start checkout: ' + (e?.response?.data?.detail || e.message));
      setBusyTier(null);
    }
  };

  if (!shouldShow) return null;

  const cycles = [...(matrix?.cycles || [])];
  if (sub?.v2?.billing_cycle === 'quarterly') cycles.splice(1, 0, { code: 'quarterly', label: 'Quarterly', legacy: true });
  const founding = matrix?.founding;
  const cycleLabel = cycles.find((c) => c.code === cycle)?.label || cycle;

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto"
         style={{ background: 'rgba(11, 13, 23, 0.92)' }} data-testid="plan-selection-modal">
      <div className="w-full max-w-7xl px-4 sm:px-6 py-10 relative">
        {dismissible && (
          <button onClick={() => setForceOpen(false)} data-testid="plan-modal-close" aria-label="Close"
            className="absolute right-4 top-4 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-slate-300 hover:text-white hover:bg-white/10">
            <X size={14} /> Close
          </button>
        )}
        <header className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-orange-500/15 text-orange-300 ring-1 ring-orange-500/40 mb-4">
            <Sparkles size={11} /> Choose your plan to continue
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" style={{ letterSpacing: '-0.03em' }}>
            Pick the plan that fits your clinic.
          </h1>
          <p className="mt-3 text-slate-300 text-sm sm:text-base max-w-2xl mx-auto">
            {sub.v2.beta_migrated
              ? `You have ${sub.v2.days_left ?? 15} days on the house to explore — pick a plan before it lapses to keep working without interruption.`
              : 'Prices exclude 18% GST. Cancel anytime.'}
          </p>
        </header>

        <CycleToggle cycles={cycles} cycle={cycle} setCycle={setCycle} founding={founding} />
        {cycle === 'annual' && founding?.available && <FoundingBanner founding={founding} />}

        <AddonSteppers
          addons={matrix?.addons || {}}
          extraLocs={extraLocs} setExtraLocs={setExtraLocs}
          extraUsers={extraUsers} setExtraUsers={setExtraUsers}
          smartMkt={smartMkt} setSmartMkt={setSmartMkt}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="plan-cards">
          {['clinic', 'chain'].map((tier) => (
            <PlanCard
              key={tier}
              tier={tier}
              plan={matrix?.tiers?.[tier]}
              quote={quotes[tier]}
              cycleLabel={cycleLabel}
              highlight={intended?.plan ? tier === intended.plan : tier === 'chain'}
              disabled={!isOwner || !!busyTier || !quotes[tier] || !!quotes[tier]?.error}
              busy={busyTier === tier}
              onChoose={() => checkout(tier)}
            />
          ))}
          <EnterpriseCard plan={matrix?.enterprise} />
        </div>

        {error && (
          <div className="mx-auto max-w-3xl mt-6 px-4 py-3 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-100 text-sm" data-testid="plan-error">
            {error}
          </div>
        )}

        <p className="mt-8 text-center text-[12.5px] text-slate-400 flex items-center justify-center gap-2">
          <ShieldCheck size={14} />
          GST invoice on every payment · Cancel anytime · Prorated refund on downgrade
        </p>

        {isAdminSkip && (
          <div className="mt-6 text-center">
            <button onClick={() => sub.refresh && sub.refresh()} data-testid="admin-skip-refresh"
              className="text-[11px] text-slate-500 hover:text-slate-300 underline">
              Admin · re-check status
            </button>
          </div>
        )}
        {!isOwner && (
          <div className="mx-auto max-w-3xl mt-6 px-4 py-3 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-100 text-sm text-center">
            Ask your clinic owner to select a plan. Non-owner accounts can&apos;t start a subscription.
          </div>
        )}
      </div>
    </div>
  );
}

function CycleToggle({ cycles, cycle, setCycle, founding }) {
  const badgeFor = (c) => {
    if (c.code === 'annual' && founding?.available) return `FOUNDING · ${founding.discount_percent}% OFF`;
    return c.discount_percent ? `${c.discount_percent}% OFF` : null;
  };
  return (
    <div className="mx-auto max-w-2xl bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-1.5 flex flex-wrap justify-center gap-1 mb-4"
         data-testid="billing-cycle-toggle">
      {cycles.map((c) => {
        const badge = badgeFor(c);
        const active = cycle === c.code;
        return (
          <button key={c.code} onClick={() => setCycle(c.code)} data-testid={`cycle-${c.code}`}
            className={`relative px-5 py-2 rounded-xl text-[13px] font-semibold transition-colors ${active ? 'bg-white text-slate-900 shadow-lg' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}>
            {c.label}
            {c.legacy && <span className="ml-1.5 text-[9px] font-bold text-slate-400 uppercase">current</span>}
            {badge && (
              <span className={`ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wider ${c.code === 'annual' && founding?.available ? 'bg-orange-500 text-white' : 'bg-emerald-500/90 text-white'}`}>
                {badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function FoundingBanner({ founding }) {
  return (
    <div className="mx-auto max-w-2xl mb-5 px-4 py-2.5 rounded-xl bg-orange-500/10 border border-orange-500/40 text-orange-100 text-[13px] flex items-center justify-center gap-2"
         data-testid="founding-banner">
      <Sparkles size={14} className="text-orange-300" />
      <span>
        <b>Founding Clinic offer:</b> {founding.discount_percent}% off Annual, price locked for your next {founding.locked_renewals} renewals ·{' '}
        <b data-testid="founding-spots-left">{founding.spots_left} of {founding.limit} spots left</b>
      </span>
    </div>
  );
}

function Stepper({ icon: Icon, label, hint, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10" data-testid={testid}>
      <Icon size={14} className="text-slate-400" />
      <div className="text-[12.5px] text-slate-200">
        {label}
        <div className="text-[10.5px] text-slate-400">{hint}</div>
      </div>
      <button onClick={() => onChange(Math.max(0, value - 1))} data-testid={`${testid}-minus`}
        className="ml-2 w-7 h-7 rounded-md bg-white/10 hover:bg-white/20 text-white font-bold">−</button>
      <span className="w-7 text-center font-bold text-white" data-testid={`${testid}-count`}>{value}</span>
      <button onClick={() => onChange(Math.min(50, value + 1))} data-testid={`${testid}-plus`}
        className="w-7 h-7 rounded-md bg-white/10 hover:bg-white/20 text-white font-bold">+</button>
    </div>
  );
}

function AddonSteppers({ addons, extraLocs, setExtraLocs, extraUsers, setExtraUsers, smartMkt, setSmartMkt }) {
  const loc = addons.extra_location;
  const usr = addons.extra_user;
  const smk = addons.smart_marketing;
  return (
    <div className="mx-auto max-w-4xl mb-8 flex flex-wrap items-center justify-center gap-3" data-testid="addon-steppers">
      {loc && (
        <Stepper icon={MapPin} label="Extra locations" testid="extras"
          hint={`${fmtINR(loc.monthly_price)}/mo each · +${loc.includes_users || 0} users`}
          value={extraLocs} onChange={setExtraLocs} />
      )}
      {usr && (
        <Stepper icon={Users} label="Extra users" testid="extra-users"
          hint={`${fmtINR(usr.monthly_price)}/mo each`} value={extraUsers} onChange={setExtraUsers} />
      )}
      {smk && (
        <button onClick={() => setSmartMkt(!smartMkt)} data-testid="addon-smart-marketing" aria-pressed={smartMkt}
          className={`px-3 py-2 rounded-xl border text-[12.5px] transition-colors ${smartMkt ? 'bg-orange-500/20 border-orange-400 text-orange-100' : 'bg-white/5 border-white/10 text-slate-200 hover:bg-white/10'}`}>
          {smartMkt ? <Check size={13} className="inline mr-1 -mt-0.5" /> : <Sparkles size={13} className="inline mr-1 -mt-0.5" />}
          {smk.name} · {fmtINR(smk.monthly_price)}/mo
        </button>
      )}
    </div>
  );
}

function PlanCard({ tier, plan, quote, cycleLabel, highlight, disabled, busy, onChoose }) {
  const dark = highlight;
  const q = quote && !quote.error ? quote : null;
  return (
    <div className={`rounded-2xl p-7 shadow-2xl relative flex flex-col ${dark ? 'bg-slate-900 text-white ring-2 ring-orange-500/60' : 'bg-white border border-slate-200/70'}`}
         data-testid={`plan-card-${tier}`}>
      {dark && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-[0.18em] shadow-lg">
          <Sparkles size={11} /> Most Popular
        </span>
      )}
      <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${dark ? 'text-orange-300' : 'text-slate-500'}`}>{plan?.name || tier}</div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className={`text-4xl font-extrabold ${dark ? 'text-white' : 'text-slate-900'}`} style={{ letterSpacing: '-0.03em' }} data-testid={`plan-price-${tier}`}>
          {q ? fmtINR(q.total_amount) : '—'}
        </span>
        <span className={`text-sm ${dark ? 'text-slate-300' : 'text-slate-500'}`}>/ {q?.months === 1 ? 'month' : cycleLabel.toLowerCase()}</span>
      </div>
      {q && q.months > 1 && (
        <p className={`mt-1 text-[13px] font-semibold ${dark ? 'text-emerald-300' : 'text-emerald-600'}`} data-testid={`${tier}-monthly-eq`}>
          ≈ {fmtINR(q.monthly_equivalent)}/month{q.discount_percent > 0 && ` · Save ${q.discount_percent}%`}
          {q.founding_applied && ' · Founding price'}
        </p>
      )}
      <p className={`mt-3 text-sm ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{plan?.description}</p>
      <ul className="mt-5 space-y-2 flex-1">
        {(plan?.features || []).map((f) => (
          <li key={f} className={`flex items-start gap-2 text-[13.5px] ${dark ? 'text-slate-200' : 'text-slate-700'}`}>
            <Check size={16} strokeWidth={2.6} className={`mt-0.5 shrink-0 ${dark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {quote?.error && <p className="mt-4 text-[12px] text-rose-500" data-testid={`plan-quote-error-${tier}`}>{quote.error}</p>}
      {q && <PriceBreakdown q={q} dark={dark} tier={tier} />}
      <button onClick={onChoose} disabled={disabled} data-testid={`cta-choose-${tier}`}
        className={`mt-5 w-full py-3 rounded-xl font-bold text-sm shadow-md transition-colors disabled:bg-slate-400 ${dark ? 'text-slate-900 bg-white hover:bg-slate-100' : 'text-white bg-[#0F52BA] hover:bg-[#0C4399]'}`}>
        {busy ? 'Opening Checkout…' : `Choose ${plan?.name || tier} Plan`}
      </button>
    </div>
  );
}

function PriceBreakdown({ q, dark, tier }) {
  const row = (label, amount, testid, cls = '') => (
    <div className={`flex justify-between ${cls}`} data-testid={testid}>
      <span>{label}</span><span className="tabular-nums">{amount}</span>
    </div>
  );
  return (
    <div className={`mt-5 rounded-xl p-3 text-[12px] space-y-1 ${dark ? 'bg-white/5 text-slate-300' : 'bg-slate-50 text-slate-600'}`} data-testid={`plan-breakdown-${tier}`}>
      {row(`Plan · ${q.months} mo`, fmtINR(q.base_amount))}
      {q.extra_clinics_count > 0 && row(`Extra locations × ${q.extra_clinics_count}`, fmtINR(q.extra_clinics_amount))}
      {q.extra_users_count > 0 && row(`Extra users × ${q.extra_users_count}`, fmtINR(q.extra_users_amount))}
      {q.smart_marketing && row('Smart Marketing', fmtINR(q.smart_marketing_amount))}
      {q.discount_amount > 0 && row(`${q.founding_applied ? 'Founding offer' : 'Discount'} (${q.discount_percent}%)`, `−${fmtINR(q.discount_amount)}`, `plan-discount-${tier}`, dark ? 'text-emerald-300' : 'text-emerald-700')}
      {q.onboarding_fee > 0 && row('One-time onboarding fee', fmtINR(q.onboarding_fee), `plan-onboarding-${tier}`)}
      {q.onboarding_fee === 0 && q.billing_cycle === 'annual' && q.is_first_invoice && row('Onboarding fee', 'Waived', `plan-onboarding-${tier}`, dark ? 'text-emerald-300' : 'text-emerald-700')}
      {row(`GST (${q.gst_percent}%)`, fmtINR(q.gst_amount))}
      <div className={`flex justify-between pt-1.5 mt-1 border-t font-bold ${dark ? 'border-white/10 text-white' : 'border-slate-200 text-slate-900'}`} data-testid={`plan-total-${tier}`}>
        <span>Total due today</span><span className="tabular-nums">{fmtINR(q.grand_total)}</span>
      </div>
      <div className={`text-[10.5px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
        Includes {q.location_limit} location{q.location_limit === 1 ? '' : 's'} · {q.user_limit} users
      </div>
    </div>
  );
}

function EnterpriseCard({ plan }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="rounded-2xl p-7 shadow-2xl flex flex-col bg-gradient-to-b from-slate-800 to-slate-900 border border-white/10 text-white"
         data-testid="plan-card-enterprise">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1.5">
        <Building size={12} /> {plan?.name || 'Enterprise'}
      </div>
      <div className="mt-3 text-4xl font-extrabold" style={{ letterSpacing: '-0.03em' }}>Custom</div>
      <p className="mt-1 text-[13px] text-slate-400">Pricing tailored to your network</p>
      <p className="mt-3 text-sm text-slate-300">{plan?.description}</p>
      <ul className="mt-5 space-y-2 flex-1">
        {(plan?.features || []).map((f) => (
          <li key={f} className="flex items-start gap-2 text-[13.5px] text-slate-200">
            <Check size={16} strokeWidth={2.6} className="mt-0.5 shrink-0 text-slate-400" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button type="button" onClick={() => setOpen(true)} data-testid="cta-enterprise-talk"
        className="mt-5 w-full py-3 rounded-xl font-bold text-sm text-center text-white border border-white/30 hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2">
        <Mail size={14} /> {plan?.cta || 'Talk to us'}
      </button>
      <EnterpriseEnquiryModal open={open} onClose={() => setOpen(false)} source="plan_picker" />
    </div>
  );
}
